import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Play,
  Plus,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { PasswordInput } from "@/components/ui/PasswordInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { BASE_URL } from "@/lib/api";

// FieldKind drives which input the wizard's sample-data step renders for a
// given request body field. Endpoints declare structured fields instead of a
// raw JSON blob so the user fills a real form, not a textarea.
type FieldKind = "text" | "textarea" | "email" | "select";

interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  options?: Array<{ value: string; label: string }>;
}

// EndpointSpec describes one runnable endpoint exposed to the tester. The
// catalog below is intentionally small — we only register endpoints the
// backend actually accepts via API key today (POST /public/tickets). Other
// roles render an "endpoint coming soon" empty state until the matching
// backend route lands.
interface EndpointSpec {
  id: string;
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  summary: string;
  description?: string;
  fields: FieldSpec[];
  // When true the wizard renders a "+ Add custom field" key/value editor
  // and folds those rows into a `metadata` object on the request body. The
  // canonical fields list above stays first-class; metadata is for anything
  // outside the documented schema.
  metadataEnabled?: boolean;
  // Anchor on /apidocs that Stoplight Elements deep-links to this
  // operation. Lets the wizard say "View full API spec →" without
  // duplicating the schema documentation inline.
  docsAnchor?: string;
  // Returns the in-app route to navigate to after a successful run, given
  // the parsed response body (`{ "data": { ... } }` envelope). Returning
  // null skips the redirect.
  successPath?: (parsed: unknown) => string | null;
  // Human label for the success-redirect button — e.g. "View ticket".
  successCta?: string;
}

export const ENDPOINTS_BY_ROLE: Record<string, EndpointSpec[]> = {
  "cs.ticket_responder": [
    {
      id: "create-ticket",
      method: "POST",
      path: "/api/v1/public/tickets",
      summary: "Create ticket",
      description:
        "Posts a new support ticket as if it came from your customer-facing app. The Ticket Responder agent will pick it up from the queue.",
      fields: [
        {
          name: "subject",
          label: "Subject",
          kind: "text",
          defaultValue: "Login broken on iOS",
          required: true,
        },
        {
          name: "description",
          label: "Description",
          kind: "textarea",
          defaultValue: "User taps Sign in, app force-closes.",
          required: true,
        },
        {
          name: "priority",
          label: "Priority",
          kind: "select",
          defaultValue: "P2",
          options: [
            { value: "P1", label: "P1 — Urgent" },
            { value: "P2", label: "P2 — High" },
            { value: "P3", label: "P3 — Medium" },
            { value: "P4", label: "P4 — Low" },
          ],
        },
        {
          name: "category",
          label: "Category",
          kind: "text",
          defaultValue: "auth",
          helpText: "Free-form bucket your team uses to triage.",
        },
        {
          name: "customer_email",
          label: "Customer email",
          kind: "email",
          defaultValue: "alex@fitfab.ai",
          required: true,
        },
        {
          name: "reporter_name",
          label: "Reporter name",
          kind: "text",
          defaultValue: "Alex",
        },
      ],
      metadataEnabled: true,
      docsAnchor: "/apidocs#/operations/createTicket",
      successPath: (parsed) => {
        const ticketID = (parsed as { data?: { ticket?: { id?: string } } })
          ?.data?.ticket?.id;
        return ticketID ? `/support/${ticketID}` : null;
      },
      successCta: "View ticket",
    },
  ],
};

type WizardStep = "agent" | "fields" | "result";
type RunStatus = "idle" | "running" | "success" | "failed";

interface RunOutcome {
  status: number;
  ok: boolean;
  durationMs: number;
  parsed: unknown;
  rawBody: string;
}

export function ApiKeyTester({
  open,
  onOpenChange,
  roleCode,
  roleLabel,
  keyLabel,
  initialSecret,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  roleCode: string;
  roleLabel: string;
  keyLabel: string;
  initialSecret?: string;
}) {
  const navigate = useNavigate();
  const endpoints = ENDPOINTS_BY_ROLE[roleCode] ?? [];

  const [step, setStep] = useState<WizardStep>("agent");
  const [endpointId, setEndpointId] = useState(endpoints[0]?.id ?? "");
  const [secret, setSecret] = useState(initialSecret ?? "");
  const [values, setValues] = useState<Record<string, string>>({});
  // Ad-hoc key/value rows the user adds via "+ Add custom field". Serialised
  // into the request body's `metadata` object — leaves the canonical
  // top-level fields alone so the API contract stays predictable.
  const [customFields, setCustomFields] = useState<
    Array<{ id: string; key: string; value: string }>
  >([]);
  const [run, setRun] = useState<{ status: RunStatus; outcome?: RunOutcome }>({
    status: "idle",
  });

  const endpoint = endpoints.find((e) => e.id === endpointId);

  // Reset the wizard each time the dialog re-opens (or the role/key changes).
  // Without this a previous run's response would leak into the next session.
  const lastInitialKey = useRef(initialSecret);
  useEffect(() => {
    if (!open) return;
    setStep("agent");
    setEndpointId(endpoints[0]?.id ?? "");
    setRun({ status: "idle" });
    if (initialSecret && initialSecret !== lastInitialKey.current) {
      setSecret(initialSecret);
      lastInitialKey.current = initialSecret;
    } else if (!initialSecret) {
      setSecret("");
    }
    // Seed sample values from endpoint defaults.
    const seed: Record<string, string> = {};
    for (const f of endpoints[0]?.fields ?? []) {
      seed[f.name] = f.defaultValue ?? "";
    }
    setValues(seed);
    setCustomFields([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleCode]);

  // When the user picks a different endpoint mid-wizard, reseed defaults.
  useEffect(() => {
    if (!endpoint) return;
    const seed: Record<string, string> = {};
    for (const f of endpoint.fields) {
      seed[f.name] = f.defaultValue ?? "";
    }
    setValues(seed);
    setCustomFields([]);
  }, [endpointId]);

  const requestBody = useMemo(() => {
    if (!endpoint) return "";
    const body: Record<string, unknown> = {};
    for (const f of endpoint.fields) {
      const v = values[f.name];
      if (v !== undefined && v !== "") body[f.name] = v;
    }
    if (endpoint.metadataEnabled) {
      const metadata: Record<string, unknown> = {};
      for (const row of customFields) {
        const key = row.key.trim();
        if (!key) continue;
        // Try to parse the value as JSON so booleans/numbers/objects round-trip
        // cleanly; fall back to the raw string for anything that isn't valid
        // JSON. Empty string → skip the row entirely.
        const raw = row.value;
        if (raw === "") continue;
        try {
          metadata[key] = JSON.parse(raw);
        } catch {
          metadata[key] = raw;
        }
      }
      if (Object.keys(metadata).length > 0) body.metadata = metadata;
    }
    return JSON.stringify(body, null, 2);
  }, [endpoint, values, customFields]);

  const curlSnippet = useMemo(() => {
    if (!endpoint) return "";
    const parts = [
      `curl -X ${endpoint.method} '${BASE_URL}${endpoint.path}'`,
      `  -H 'X-VirtualDesk-API-Key: ${secret || "<paste-your-key>"}'`,
      `  -H 'Content-Type: application/json'`,
    ];
    if (endpoint.method !== "GET" && requestBody.trim()) {
      const escaped = requestBody.replace(/'/g, `'\\''`);
      parts.push(`  -d '${escaped}'`);
    }
    return parts.join(" \\\n");
  }, [endpoint, secret, requestBody]);

  const missingRequiredField = endpoint?.fields.find(
    (f) => f.required && !values[f.name]?.trim(),
  );
  const canRun = !!endpoint && !!secret.trim() && !missingRequiredField;

  const onRun = async () => {
    if (!endpoint || !canRun) return;
    setStep("result");
    setRun({ status: "running" });
    const started = performance.now();
    try {
      const res = await fetch(`${BASE_URL}${endpoint.path}`, {
        method: endpoint.method,
        headers: {
          "X-VirtualDesk-API-Key": secret.trim(),
          "Content-Type": "application/json",
        },
        body: endpoint.method === "GET" ? undefined : requestBody,
      });
      const text = await res.text();
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Non-JSON response — keep raw string.
      }
      const outcome: RunOutcome = {
        status: res.status,
        ok: res.ok,
        durationMs: Math.round(performance.now() - started),
        parsed,
        rawBody: text,
      };
      setRun({ status: res.ok ? "success" : "failed", outcome });
      if (!res.ok) {
        const message = extractErrorMessage(parsed) ?? `HTTP ${res.status}`;
        toast.error("Test failed", { description: message });
      }
    } catch (err) {
      const message = (err as Error).message;
      setRun({
        status: "failed",
        outcome: {
          status: 0,
          ok: false,
          durationMs: Math.round(performance.now() - started),
          parsed: { error: { message } },
          rawBody: message,
        },
      });
      toast.error("Test failed", { description: message });
    }
  };

  const onGoToTracking = () => {
    if (!endpoint?.successPath || !run.outcome) return;
    const path = endpoint.successPath(run.outcome.parsed);
    if (!path) return;
    onOpenChange(false);
    navigate(path);
  };

  const successPath =
    run.status === "success" && run.outcome && endpoint?.successPath
      ? endpoint.successPath(run.outcome.parsed)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test API key</DialogTitle>
          <DialogDescription>
            Run a real request against the live backend with the key labeled{" "}
            <span className="font-mono text-foreground">{keyLabel}</span>.
          </DialogDescription>
        </DialogHeader>

        <Stepper step={step} hasEndpoints={endpoints.length > 0} />

        {endpoints.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
            No public endpoint is wired up for{" "}
            <span className="font-mono text-foreground">{roleCode}</span> yet.
            Endpoints land as each agent role ships its public surface.
          </div>
        ) : step === "agent" ? (
          <AgentStep
            roleCode={roleCode}
            roleLabel={roleLabel}
            endpoints={endpoints}
            endpointId={endpointId}
            onEndpointChange={setEndpointId}
            secret={secret}
            onSecretChange={setSecret}
            onCancel={() => onOpenChange(false)}
            onContinue={() => setStep("fields")}
            canContinue={!!endpoint && !!secret.trim()}
          />
        ) : step === "fields" ? (
          <FieldsStep
            endpoint={endpoint!}
            values={values}
            onChange={(name, v) => setValues((prev) => ({ ...prev, [name]: v }))}
            customFields={customFields}
            onAddCustomField={() =>
              setCustomFields((prev) => [
                ...prev,
                { id: crypto.randomUUID(), key: "", value: "" },
              ])
            }
            onUpdateCustomField={(id, patch) =>
              setCustomFields((prev) =>
                prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
              )
            }
            onRemoveCustomField={(id) =>
              setCustomFields((prev) => prev.filter((row) => row.id !== id))
            }
            requestBody={requestBody}
            curlSnippet={curlSnippet}
            onBack={() => setStep("agent")}
            onRun={onRun}
            canRun={canRun}
            missingFieldLabel={missingRequiredField?.label}
          />
        ) : (
          <ResultStep
            status={run.status}
            outcome={run.outcome}
            successCta={endpoint?.successCta}
            successPath={successPath}
            onClose={() => onOpenChange(false)}
            onRetry={() => setStep("fields")}
            onGoToTracking={onGoToTracking}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ step, hasEndpoints }: { step: WizardStep; hasEndpoints: boolean }) {
  if (!hasEndpoints) return null;
  const steps: Array<{ id: WizardStep; label: string }> = [
    { id: "agent", label: "Agent" },
    { id: "fields", label: "Sample data" },
    { id: "result", label: "Result" },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center gap-2 pt-1 pb-2">
      {steps.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.id} className="flex flex-1 items-center gap-2">
            <div
              className={
                "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium " +
                (done
                  ? "bg-brand-violet text-white"
                  : active
                    ? "border border-brand-violet text-brand-violet"
                    : "border border-border text-muted-foreground")
              }
            >
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span
              className={
                "text-xs " +
                (active ? "text-foreground font-medium" : "text-muted-foreground")
              }
            >
              {s.label}
            </span>
            {i < steps.length - 1 ? (
              <div className="h-px flex-1 bg-border/60" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function AgentStep({
  roleCode,
  roleLabel,
  endpoints,
  endpointId,
  onEndpointChange,
  secret,
  onSecretChange,
  onCancel,
  onContinue,
  canContinue,
}: {
  roleCode: string;
  roleLabel: string;
  endpoints: EndpointSpec[];
  endpointId: string;
  onEndpointChange: (id: string) => void;
  secret: string;
  onSecretChange: (v: string) => void;
  onCancel: () => void;
  onContinue: () => void;
  canContinue: boolean;
}) {
  const endpoint = endpoints.find((e) => e.id === endpointId);
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Agent under test
        </p>
        <p className="text-sm font-medium">{roleLabel}</p>
        <p className="font-mono text-[11px] text-muted-foreground">{roleCode}</p>
      </div>

      {endpoints.length > 1 ? (
        <div className="space-y-1.5">
          <Label>Endpoint to exercise</Label>
          <Select value={endpointId} onValueChange={onEndpointChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {endpoints.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.summary}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {endpoint ? (
            <p className="font-mono text-[11px] text-muted-foreground">
              <span className="text-brand-violet">{endpoint.method}</span>{" "}
              {endpoint.path}
            </p>
          ) : null}
        </div>
      ) : endpoint ? (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 space-y-1">
          <p className="font-mono text-xs">
            <span className="text-brand-violet">{endpoint.method}</span>{" "}
            {endpoint.path}
          </p>
          <p className="text-xs text-muted-foreground">{endpoint.summary}</p>
          {endpoint.description ? (
            <p className="text-[11px] text-muted-foreground">{endpoint.description}</p>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="tester-secret">API key</Label>
        <PasswordInput
          id="tester-secret"
          placeholder="Paste the full key — only shown once at mint time"
          value={secret}
          onChange={(e) => onSecretChange(e.target.value)}
          autoComplete="off"
        />
        <p className="text-[11px] text-muted-foreground">
          The key never leaves your browser — it's sent directly to the backend
          on Run, not logged anywhere on the client.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function FieldsStep({
  endpoint,
  values,
  onChange,
  customFields,
  onAddCustomField,
  onUpdateCustomField,
  onRemoveCustomField,
  requestBody,
  curlSnippet,
  onBack,
  onRun,
  canRun,
  missingFieldLabel,
}: {
  endpoint: EndpointSpec;
  values: Record<string, string>;
  onChange: (name: string, v: string) => void;
  customFields: Array<{ id: string; key: string; value: string }>;
  onAddCustomField: () => void;
  onUpdateCustomField: (
    id: string,
    patch: Partial<{ key: string; value: string }>,
  ) => void;
  onRemoveCustomField: (id: string) => void;
  requestBody: string;
  curlSnippet: string;
  onBack: () => void;
  onRun: () => void;
  canRun: boolean;
  missingFieldLabel?: string;
}) {
  const requiredNames = endpoint.fields
    .filter((f) => f.required)
    .map((f) => f.label)
    .join(", ");
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/20 px-3 py-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-xs">
            <span className="text-brand-violet">{endpoint.method}</span>{" "}
            {endpoint.path}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {endpoint.summary}
            {requiredNames ? (
              <>
                {" · "}required: <span className="text-foreground">{requiredNames}</span>
              </>
            ) : null}
          </p>
        </div>
        {endpoint.docsAnchor ? (
          <a
            href={endpoint.docsAnchor}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-brand-violet hover:underline shrink-0"
          >
            View full API spec
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {endpoint.fields.map((field) => (
          <FieldInput
            key={field.name}
            field={field}
            value={values[field.name] ?? ""}
            onChange={(v) => onChange(field.name, v)}
          />
        ))}
      </div>

      {endpoint.metadataEnabled ? (
        <CustomFieldsEditor
          rows={customFields}
          onAdd={onAddCustomField}
          onUpdate={onUpdateCustomField}
          onRemove={onRemoveCustomField}
        />
      ) : null}

      <details className="rounded-md border border-border/60 bg-muted/20 p-2">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          Request body
        </summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre font-mono text-[11px] text-foreground">
{requestBody || "{}"}
        </pre>
      </details>

      <details className="rounded-md border border-border/60 bg-muted/20 p-2">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          Equivalent curl command
        </summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre font-mono text-[11px] text-foreground">
{curlSnippet}
        </pre>
      </details>

      {missingFieldLabel ? (
        <p className="text-[11px] text-signal-awaiting">
          Fill the {missingFieldLabel} field to run the test.
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button onClick={onRun} disabled={!canRun}>
          <Play className="h-4 w-4" />
          Run test
        </Button>
      </div>
    </div>
  );
}

function CustomFieldsEditor({
  rows,
  onAdd,
  onUpdate,
  onRemove,
}: {
  rows: Array<{ id: string; key: string; value: string }>;
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<{ key: string; value: string }>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/10 p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium">Custom fields (metadata)</p>
          <p className="text-[11px] text-muted-foreground">
            Anything outside the canonical schema goes here. Keys are free-form;
            values can be plain strings or valid JSON (true / 42 / {"{...}"}).
            Capped at 64KB total.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" />
          Add field
        </Button>
      </div>
      {rows.length > 0 ? (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start"
            >
              <Input
                placeholder="key (e.g. app_version)"
                value={row.key}
                onChange={(e) => onUpdate(row.id, { key: e.target.value })}
                className="font-mono text-xs"
              />
              <Input
                placeholder='value — string, or JSON like 42, true, {"a":1}'
                value={row.value}
                onChange={(e) => onUpdate(row.id, { value: e.target.value })}
                className="font-mono text-xs"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(row.id)}
                className="px-2 text-muted-foreground hover:text-signal-failed"
                aria-label="Remove custom field"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FieldSpec;
  value: string;
  onChange: (v: string) => void;
}) {
  const span = field.kind === "textarea" ? "sm:col-span-2" : "";
  return (
    <div className={"space-y-1.5 " + span}>
      <Label htmlFor={`tester-field-${field.name}`}>
        {field.label}
        {field.required ? <span className="ml-1 text-signal-failed">*</span> : null}
      </Label>
      {field.kind === "textarea" ? (
        <Textarea
          id={`tester-field-${field.name}`}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      ) : field.kind === "select" ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={`tester-field-${field.name}`}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={`tester-field-${field.name}`}
          type={field.kind === "email" ? "email" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      )}
      {field.helpText ? (
        <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
      ) : null}
    </div>
  );
}

function ResultStep({
  status,
  outcome,
  successCta,
  successPath,
  onClose,
  onRetry,
  onGoToTracking,
}: {
  status: RunStatus;
  outcome?: RunOutcome;
  successCta?: string;
  successPath: string | null;
  onClose: () => void;
  onRetry: () => void;
  onGoToTracking: () => void;
}) {
  const responseText = useMemo(() => {
    if (!outcome) return "";
    try {
      return JSON.stringify(outcome.parsed, null, 2);
    } catch {
      return outcome.rawBody;
    }
  }, [outcome]);

  if (status === "running") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-violet" />
        <p className="text-sm text-muted-foreground">Running test against live backend…</p>
      </div>
    );
  }

  const ok = status === "success";
  const statusBadge = (
    <span
      className={
        "rounded-full px-2 py-0.5 font-mono text-[11px] " +
        (ok
          ? "bg-signal-ok/15 text-signal-ok"
          : "bg-signal-failed/15 text-signal-failed")
      }
    >
      {outcome?.status === 0 ? "network error" : `HTTP ${outcome?.status ?? "?"}`}
    </span>
  );

  return (
    <div className="space-y-4">
      <div
        className={
          "rounded-md border p-4 flex items-start gap-3 " +
          (ok
            ? "border-signal-ok/40 bg-signal-ok/5"
            : "border-signal-failed/40 bg-signal-failed/5")
        }
      >
        {ok ? (
          <CheckCircle2 className="h-5 w-5 text-signal-ok shrink-0" />
        ) : (
          <XCircle className="h-5 w-5 text-signal-failed shrink-0" />
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <p className={"text-sm font-medium " + (ok ? "text-signal-ok" : "text-signal-failed")}>
            {ok ? "Test successful" : "Test failed"}
          </p>
          <p className="text-xs text-muted-foreground">
            {ok
              ? "The backend accepted the request — the agent will pick this up from its queue."
              : "The backend rejected the request. See the response below for details."}
          </p>
          <div className="flex items-center gap-3 pt-1 text-xs">
            {statusBadge}
            <span className="text-muted-foreground">{outcome?.durationMs} ms</span>
          </div>
        </div>
      </div>

      <details className="rounded-md border border-border/60 bg-muted/20 p-2" open={!ok}>
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          Response body
        </summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] text-foreground">
{responseText}
        </pre>
      </details>

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {ok && successPath ? (
          <Button onClick={onGoToTracking}>
            {successCta ?? "View result"}
            <ExternalLink className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="outline" onClick={onRetry}>
            <ArrowLeft className="h-4 w-4" />
            Edit and retry
          </Button>
        )}
      </div>
    </div>
  );
}

function extractErrorMessage(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== "object") return null;
  const env = parsed as { error?: { message?: string } };
  return env.error?.message ?? null;
}
